import Layout from 'components/layout';

import React, { useEffect, type FC, type PropsWithChildren, useState, type ChangeEvent } from 'react';

import { graphql, type HeadFC } from 'gatsby';
import {
  AccessibleDescription,
  Box,
  Combobox,
  Container,
  Heading,
  Hero,
  Link,
  LucidHead,
  PreFooterCta,
  SimplePagination,
  LogoSection,
  ResourceCard,
  SearchBar,
  LoadingIndicator,
  Image,
  reactHTMLParser,
  Text,
  StatGrid,
} from '@lucid/components';
import { isBrowser, isDefined, withURL } from '@lucid/utils';
import { useUrlPathUpdate, useSmartlingStrings, withImage } from '@lucid/hooks';

import { useCaseStudySearch } from 'hooks/hooks';
import type { Lucid } from 'types/types';

// TYPES
export interface CollectionCard {
  /** The id of the directus item */
  id: string;
  /** The date of the webinar to be displayed above the `Card` heading */
  date?: string;
  /** The image to be displayed at the top of the `Card`. */
  image?: Lucid.Directus.Generated.Directus_Files;
  /** The text to be displayed in the heading of the `Card`. */
  heading?: string;
  /** The text to be displayed in the subheading of the `Card`. */
  subheading?: string;
  /** The image src to be displayed at the top of the `Card`. */
  imageSrc?: string;
  /** The url that the `Card` will link to. */
  url?: string;
  /** The Lucid products that are related to the `Card`. */
  relatedProducts?: Lucid.Directus.Generated.Ebook_Lucid_Product[];
  /** The related industires related to the `Card` for filtering. */
  industries?: Lucid.Directus.Generated.Ebook_Collection_Industry[];
  /** The related teams related to the `Card` for filtering. */
  teams?: Lucid.Directus.Generated.Ebook_Collection_Team[];
}

interface Filter {
  value: string;
  label: string;
}

// CSS
const cardGridCss = (theme: Lucid.Theme.Brand) => ({
  display: 'grid',
  gridRowGap: theme.space.s40,
  width: '100%',
  [theme.breakpoints.bp768.min]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridColumnGap: theme.space.s40,
  },
  [theme.breakpoints.bp1220.min]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
    gridColumnGap: theme.space.s40,
  },
});

const modifyData = (collectionData: Lucid.Directus.Generated.Case_Study[], __typename?: string) =>
  collectionData.reduce<CollectionCard[]>((previous, collection) => {
    const card = collection as Lucid.Directus.Generated.Case_Study;
    const cardTranslatedData = card.translations?.[0];

    return [
      ...previous,
      {
        id: card.id,
        image: card.hub_logo || card.logo,
        heading: card.company_name,
        subheading: cardTranslatedData?.summary,
        imageSrc: card.hub_logo?.filename_disk || card.logo?.filename_disk,
        industries: card.industries,
        teams: card.teams,
        url: cardTranslatedData?.path?.url,
        relatedProducts: card.products,
      },
    ] as CollectionCard[];
  }, [] as CollectionCard[]);

const Customer: FC<PropsWithChildren<Lucid.Context.PageProps>> = ({ data, pageContext }) => {
  const { nid, pageType, urlLangPrefix, localeId, shortCode, shouldHideSsr, metatags, product, alias } = pageContext;
  const { directus } = data;
  const node = directus.node;
  const { nodeTranslations } = node;
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Customer_Translations;
  const {
    hero_image,
    hero_heading,
    hero_text,
    hero_link_set,
    logo_banner_section,
    customer_items,
    stat_source,
    stat_blocks,
    award_banner_section,
    pre_footer_heading,
    pre_footer_link,
  } = translatedData;
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_learning_campus_collection_filter_team',
      'mktg_learning_campus_collection_filter_industry',
      'mktg_learning_campus_collection_filter_topic',
      'mktg_ct_cs_studies_header',
      'mktg_ct_cs_search',
      'mktg_ct_cs_search_description',
      'mktg_generics_search_result_count',
      'mktg_read_more_about',
      'mktg_learning_campus_collection_tags',
    ],
    shortCode,
  });

  const customerItems = (customer_items?.map(bg => bg.back_n_forth_id) || []).filter(isDefined);

  const heroCta = hero_link_set?.map(link => link.links_id).filter(isDefined)[0];
  const logoList = logo_banner_section?.logo_section?.logo_list
    ?.map(logo => logo.external_logo_id?.name)
    .filter(isDefined) as string[];
  const awardList = award_banner_section?.logo_section?.logo_list
    ?.map(logo => logo.external_logo_id?.name)
    .filter(isDefined) as string[];
  const statBlocks = stat_blocks?.map(stat => stat.stat_block_id).filter(isDefined);

  // CARDS
  const allCollectionData = directus.case_study as Lucid.Directus.Generated.Case_Study[];
  const [allCardsData, setAllCardsData] = useState<CollectionCard[]>(modifyData(allCollectionData));

  useEffect(() => {
    setAllCardsData(modifyData(allCollectionData));
  }, [allCollectionData, node]);

  // SEARCH
  const [cards, setCards] = useState<CollectionCard[]>(allCardsData);
  const [collectionIndustry, setCollectionIndustry] = useState('');
  const [collectionTeam, setCollectionTeam] = useState('');
  const [page, setPage] = useState<number>(1);
  const searchBarRef = React.createRef<HTMLInputElement>();

  useUrlPathUpdate(withURL(`${alias || ''}/${page && page > 1 ? `${page}` : ''}`, { urlLangPrefix, product }));

  const { isLoading, handleSearch, searchQuery } = useCaseStudySearch({
    collectionCards: allCardsData || [],
    setCards,
    page,
    setPage,
    searchBarRef,
    collectionIndustry,
    collectionTeam,
  });

  const handleNewPage = (value: number) => {
    setPage(value);

    if (isBrowser()) {
      searchBarRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  };

  // FILTERS
  const filters = [...directus.collection_team, ...directus.collection_industry];
  const filterGroups: Record<string, Array<Filter>> = {
    DirectusData_collection_team: [{ value: '', label: withSmartling('mktg_learning_campus_collection_filter_team') }],
    DirectusData_collection_industry: [
      { value: '', label: withSmartling('mktg_learning_campus_collection_filter_industry') },
    ],
  };

  filters.map(filter => {
    const key = filter.__typename as keyof typeof filterGroups;
    const name = filter.name;
    // TODO Directus - I didn't look for translations during migration
    const label = filter.entityTranslation ? filter.entityTranslation.entityLabel : name;

    if (name && label) {
      filterGroups[key].push({ value: name, label });
    }
  });

  const filtersArray = [
    {
      placeholder: withSmartling('mktg_learning_campus_collection_filter_industry'),
      onChange: (value: string) => setCollectionIndustry(value),
      ariaLabel: withSmartling('mktg_learning_campus_collection_filter_industry'),
      startValue: filterGroups.DirectusData_collection_industry.find(
        groupIndustry => groupIndustry.label === collectionIndustry,
      )?.label,
      options: filterGroups.DirectusData_collection_industry,
    },
    {
      placeholder: withSmartling('mktg_learning_campus_collection_filter_team'),
      onChange: (value: string) => setCollectionTeam(value),
      ariaLabel: withSmartling('mktg_learning_campus_collection_filter_team'),
      startValue: filterGroups.DirectusData_collection_team.find(groupSize => groupSize.label === collectionTeam)
        ?.label,
      options: filterGroups.DirectusData_collection_team,
    },
  ];

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      {hero_heading && (
        <Container>
          <Hero
            isLeftAligned
            shouldImageStack
            willDisplayCtaAsLink
            css={{
              '& div': {
                width: '100%',
                maxWidth: '604px',
                justifyContent: 'left',
              },
              '& h1, & p, & a': {
                textAlign: 'left',
              },
            }}
            cta={heroCta}
            header={hero_heading}
            headerVariant="hero5XL700"
            iconAnimation="jumpLink"
            image={hero_image}
            subheader={hero_text || ''}
            subheaderVariant="paragraphL400"
          />
        </Container>
      )}
      <Box
        css={theme => [
          {
            width: '100%',
            overflow: 'hidden',
            margin: `${theme.space.s24}px ${theme.space.s0}px`,
            [theme.breakpoints.bp900.min]: {
              marginTop: theme.space.s64,
              marginBottom: theme.space.s64,
            },
            backgroundColor: theme.colors.coolGrey[100],
            '& ul': {
              marginTop: theme.space.s24,
              marginBottom: theme.space.s24,
              [theme.breakpoints.bp900.min]: {
                marginTop: theme.space.s32,
                marginBottom: theme.space.s32,
              },
            },
          },
        ]}
      >
        <LogoSection height={40} logos={logoList} logosColor="white" variant="carousel" />
      </Box>
      <Container>
        {customerItems && (
          <Box
            css={theme => ({
              display: 'grid',
              gridTemplateColumns: '1fr',
              [theme.breakpoints.bp900.min]: {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
              columnGap: theme.space.s32,
              rowGap: theme.space.s40,
            })}
          >
            {customerItems.map(({ heading, html, link, image, id }) => (
              <Box
                key={`customer-item-${id}`}
                css={theme => ({
                  padding: `${theme.space.s48}px ${theme.space.s32}px`,
                  border: `1px solid ${theme.colors.coolGrey[20]}`,
                })}
              >
                <Image css={{ width: '135px' }} imageAlt="" imageSrc={withImage(image)} />
                <Heading as="h3" variant="hero2XL700">
                  {heading}
                </Heading>
                <Text as="div" variant="paragraphS400">
                  {reactHTMLParser(html)}
                </Text>
                <Link
                  css={theme => ({ margin: `${theme.space.s24}px 0` })}
                  href={link?.final_url}
                  iconAnimation="navigational"
                  variant="primaryAlternate"
                >
                  {link?.default_link_text}
                </Link>
              </Box>
            ))}
          </Box>
        )}
        {award_banner_section && (
          <Box
            css={theme => ({
              borderRadius: theme.radii.r24,
              [theme.breakpoints.bp900.min]: {
                padding: `${theme.space.s80}px ${theme.space.s120}px`,
              },
              marginTop: theme.space.s64,
              marginBottom: theme.space.s64,
            })}
          >
            <Heading
              as="h2"
              css={theme => ({
                textAlign: 'center',
                marginBottom: theme.space.s64,
              })}
              variant="headingXL"
            >
              {award_banner_section.heading}
            </Heading>
            {!!awardList?.length && (
              <LogoSection height={140} logos={awardList} logosColor="original" variant="fiveWrap" />
            )}
            {award_banner_section.link?.final_url && (
              <Box
                css={theme => ({
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: theme.space.s64,
                })}
              >
                <Link
                  newTab
                  css={theme => ({
                    fontSize: theme.fontSizes.fs16,
                  })}
                  href={award_banner_section.link.final_url}
                  iconAnimation="navigational"
                  variant="primaryAlternate"
                >
                  {award_banner_section.link.default_link_text}
                </Link>
              </Box>
            )}
          </Box>
        )}
      </Container>
      {!!statBlocks?.length && (
        <Box css={theme => ({ backgroundColor: theme.colors.blue[80] })}>
          <StatGrid css={{ padding: 0 }} items={statBlocks} variant="squareCards" />
          <Container
            css={theme => ({
              color: theme.colors.white,
              gap: theme.space.s24,
              paddingBottom: theme.space.s120,
            })}
          >
            <Text css={{ marginLeft: 0 }}>{stat_source}</Text>
          </Container>
        </Box>
      )}
      <Container
        css={theme => ({
          paddingBottom: theme.space.s24,
          paddingTop: theme.space.s48,
          maxWidth: '750px',
          [theme.breakpoints.bp1220.min]: {
            maxWidth: '90rem',
          },
        })}
        id="case-studies"
      >
        <Box
          css={theme => ({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingBottom: theme.space.s40,
            textAlign: 'center',
            flexDirection: 'column',
            [theme.breakpoints.bp1220.min]: {
              textAlign: 'left',
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingBottom: theme.space.s64,
            },
          })}
        >
          <Heading as="h2" variant="heading2XL">
            {withSmartling('mktg_ct_cs_studies_header')}
          </Heading>
          <Box>
            <AccessibleDescription
              isForScreenReaderOnly
              id="collection-search-description"
              SRText={withSmartling('mktg_ct_cs_search_description')}
            />
            <SearchBar
              ref={searchBarRef}
              aria-describedby="collection-search-description"
              defaultValue={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleSearch(e.target.value);
              }}
              placeholder={withSmartling('mktg_ct_cs_search')}
            />
          </Box>
        </Box>
        <Box
          css={theme => ({
            flex: '0 1 100%',
            display: 'flex',
            flexFlow: 'row wrap',
            rowGap: theme.space.s24,
            justifyContent: 'center',
            marginBottom: theme.space.s64,
            [theme.breakpoints.bp1220.min]: {
              rowGap: 0,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
            },
          })}
        >
          {filtersArray.map((filter, index) => (
            <Combobox
              key={`filter-${filter.options[index].value}`}
              css={theme => ({
                width: '400px',
                maxWidth: '100%',
                flexBasis: 'auto',
                flexGrow: 0,
                [theme.breakpoints.bp1220.min]: { width: '300px' },
              })}
              id={`filter-${index}`}
              onChange={optionSelected => filter.onChange(optionSelected.value)}
            >
              <Combobox.Box variant="thickBottomLine">
                <Combobox.Input
                  hasCarot
                  aria-label={filter.ariaLabel}
                  placeholder={filter.placeholder}
                  startValue={filter.startValue}
                />
              </Combobox.Box>
              <Combobox.List variant="blueHighlight">
                {filter.options.map(filter => (
                  <Combobox.Option key={filter.value} text={filter.label} value={filter.value} />
                ))}
              </Combobox.List>
            </Combobox>
          ))}
        </Box>
        <Box css={{ position: 'relative' }}>
          {(!page || isLoading) && (
            <Box
              aria-busy={isLoading}
              aria-live="polite"
              css={theme => ({
                zIndex: theme.zIndices.z1000,
                backgroundColor: theme.colors.white,
                position: 'absolute',
                top: 0,
                left: 0,
                right: `-${theme.space.s16}px`,
                bottom: 0,
              })}
            >
              <LoadingIndicator />
            </Box>
          )}
          {cards && (
            <>
              <AccessibleDescription
                isForScreenReaderOnly
                aria-live="polite"
                id="collection-search-count"
                SRText={withSmartling('mktg_generics_search_result_count', {
                  count: cards ? cards.length.toString() : '0',
                })}
              />
              <SimplePagination
                aria-describedby="collection-search-count"
                items={cards}
                itemsPerPage={12}
                onClick={handleNewPage}
                startPage={page}
              >
                {({ visibleItems }) => (
                  <Box css={theme => cardGridCss(theme)}>
                    {visibleItems?.map(card => {
                      const cardImage = card.image as Lucid.Directus.Generated.Directus_Files;
                      const tags = card.relatedProducts?.length
                        ? card.relatedProducts
                            ?.map(rp => rp.lucid_product_id?.name as Lucid.Site.Name)
                            .filter(isDefined)
                        : undefined;

                      return (
                        <ResourceCard
                          key={`collection-base-resourceCard-${card.id}`}
                          shouldTruncateText
                          shouldUnifyImageHeight
                          heading={card.heading}
                          label={{ value: card.date }}
                          link={{
                            isFullCardLink: true,
                            final_url: card.url,
                            accessibleDescription: withSmartling('mktg_read_more_about', {
                              title: card.heading || '',
                            }),
                          }}
                          media={{
                            image: cardImage,
                          }}
                          tags={{
                            label: withSmartling('mktg_learning_campus_collection_tags'),
                            items: tags,
                          }}
                          text={{ html: card.subheading }}
                        />
                      );
                    })}
                  </Box>
                )}
              </SimplePagination>
            </>
          )}
        </Box>
      </Container>
      {pre_footer_link && (
        <PreFooterCta
          backgroundColor="coolGrey.100"
          ctas={[pre_footer_link]}
          data-ab="pre-footer-cta-section_container"
          heading={pre_footer_heading}
          headingVariant="hero4XL400"
          variant="secondary"
        />
      )}
    </Layout>
  );
};

export default Customer;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query CustomerQuery($nid: ID!, $shortCode: String!) {
    directus {
      node: customer_by_id(id: $nid) {
        nodeTranslations: translations {
          is_published
          languages_code {
            short_code
          }
          path {
            url
          }
        }
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          hero_heading
          hero_text
          hero_image {
            ...mediaFragment
          }
          hero_link_set {
            links_id {
              final_url
              default_link_text
            }
          }
          logo_banner_section {
            logo_section {
              logo_list {
                order
                external_logo_id {
                  name
                }
              }
            }
          }
          customer_items {
            back_n_forth_id {
              id
              heading
              html
              image {
                ...mediaFragment
              }
              logo {
                ...mediaFragment
              }
              background_image {
                ...mediaFragment
              }
              link {
                ...linkFragment
              }
            }
          }
          award_banner_section {
            heading
            logo_section {
              logo_list {
                order
                external_logo_id {
                  name
                }
              }
            }
            link {
              ...linkFragment
            }
          }
          stat_source
          stat_blocks {
            stat_block_id {
              id
              heading
              text
              image {
                ...mediaFragment
              }
            }
          }
          pre_footer_heading
          pre_footer_link {
            final_url
            default_link_text
          }
        }
      }
      case_study(
        filter: {
          _and: [
            { status: { _eq: "published" } }
            { is_private: { _neq: true } }
            { translations: { languages_code: { short_code: { _eq: $shortCode } } } }
          ]
        }
        sort: ["sort", "company_name"]
      ) {
        id
        products {
          lucid_product_id {
            name
          }
        }
        teams {
          collection_team_id {
            name
          }
        }
        industries {
          collection_industry_id {
            name
          }
        }
        hub_logo {
          ...mediaFragment
        }
        logo {
          ...mediaFragment
        }
        company_name
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          path {
            url
          }
          search_terms
          summary
        }
      }
      collection_team {
        __typename
        name
      }
      collection_industry {
        __typename
        name
      }
    }
  }
`;
